import React, { createContext, useContext, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(() => {
    try { return JSON.parse(localStorage.getItem('dl_user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('dl_token'));
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_BASE}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Credenciales inválidas');
      localStorage.setItem('dl_token', data.token);
      localStorage.setItem('dl_user',  JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('dl_token');
    localStorage.removeItem('dl_user');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((newData) => {
    const updated = { ...JSON.parse(localStorage.getItem('dl_user') || '{}'), ...newData };
    localStorage.setItem('dl_user', JSON.stringify(updated));
    setUser(updated);
  }, []);

  // apiFetch con manejo de errores robusto
  const apiFetch = useCallback(async (path, options = {}) => {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });
      if (res.status === 401) { logout(); return null; }
      const text = await res.text();
      if (!text) return null;
      try { return JSON.parse(text); } catch { return null; }
    } catch (e) {
      console.error('apiFetch error:', path, e);
      return null;
    }
  }, [token, logout]);

  // apiUpload para multipart/form-data (sin Content-Type, lo pone el browser)
  const apiUpload = useCallback(async (path, formData) => {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.status === 401) { logout(); return null; }
      const text = await res.text();
      if (!text) return null;
      try { return JSON.parse(text); } catch { return null; }
    } catch (e) {
      console.error('apiUpload error:', path, e);
      return null;
    }
  }, [token, logout]);

  // apiDownload para obtener el token en URL de descarga
  const apiDownloadUrl = useCallback((path) => {
    return `${API_BASE}${path}&token=${token}`;
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, apiFetch, apiUpload, apiDownloadUrl, loading, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
